<script setup lang="ts">
import {onMounted, ref, watch} from "vue";
import {CalendarDate, type DateValue} from "@internationalized/date";
import {Popover, PopoverContent, PopoverTrigger} from "@/core/components/ui/popover";
import {Calendar} from "@/core/components/ui/calendar";
import {CalendarIcon} from "@lucide/vue";
import {cn} from "@/lib/utils";

/**
 * A date input combining free typing and a date picker.
 *
 * Typing digits auto-inserts the "/" separators (JJ/MM/AAAA) and rejects
 * impossible segments as they are typed (day > 31, month > 12, "00"…); a
 * single high digit is auto-padded ("4" → "04/"). Impossible combinations
 * (e.g. 31/02) and incomplete dates are flagged invalid. The calendar button
 * opens a picker. The model is the formatted "DD/MM/YYYY" string; the `valid`
 * model reports whether the value is empty or a full, valid date.
 */
const {placeholder = "JJ/MM/AAAA", inputClass, disabled = false, minValue, maxValue} = defineProps<{
    placeholder?: string;
    inputClass?: string;
    disabled?: boolean;
    /** Lower bound (inclusive) accepted by typing and the calendar. */
    minValue?: DateValue;
    /** Upper bound (inclusive) accepted by typing and the calendar. */
    maxValue?: DateValue;
}>();

const model = defineModel<string>({default: ""});
/** Whether the current value is empty or a complete, valid date. */
const valid = defineModel<boolean>("valid", {default: true});

const text = ref<string>(model.value ?? "");
const open = ref<boolean>(false);
const dateValue = ref<DateValue>();
const error = ref<boolean>(false);

type Segments = { day: string; month: string; year: string };

/**
 * Split a raw digit stream into day/month/year segments.
 *
 * Auto-pads a single high digit ("4" → "04") and returns null when a segment
 * is impossible (day 00 or > 31, month 00 or > 12) so the keystroke can be
 * rejected.
 */
const parseSegments = (digits: string): Segments | null => {
    let rest = digits;
    let day = "";
    let month = "";

    if (rest.length) {
        if (rest[0] > "3") {
            day = `0${rest[0]}`;
            rest = rest.slice(1);
        } else if (rest.length >= 2) {
            day = rest.slice(0, 2);
            rest = rest.slice(2);
        } else {
            return {day: rest, month: "", year: ""};
        }

        const dayNumber = Number(day);

        if (dayNumber < 1 || dayNumber > 31) {
            return null;
        }
    }

    if (rest.length) {
        if (rest[0] > "1") {
            month = `0${rest[0]}`;
            rest = rest.slice(1);
        } else if (rest.length >= 2) {
            month = rest.slice(0, 2);
            rest = rest.slice(2);
        } else {
            return {day, month: rest, year: ""};
        }

        const monthNumber = Number(month);

        if (monthNumber < 1 || monthNumber > 12) {
            return null;
        }
    }

    return {day, month, year: rest.slice(0, 4)};
};

/** Join the segments into "DD/MM/YYYY", appending "/" as segments complete. */
const formatSegments = (segments: Segments, isDeleting: boolean): string => {
    let output = segments.day;

    if (segments.month) {
        output += `/${segments.month}`;
    } else if (!isDeleting && segments.day.length === 2) {
        output += "/";
    }

    if (segments.year) {
        output += `/${segments.year}`;
    } else if (segments.month.length === 2 && !isDeleting) {
        output += "/";
    }

    return output;
};

/** Derive the calendar value from the typed text, only when it is a full, valid date. */
const syncDateFromText = () => {
    const digits = text.value.replace(/\D/g, "");

    if (digits.length !== 8) {
        dateValue.value = undefined;

        return;
    }

    const day = Number(digits.slice(0, 2));
    const month = Number(digits.slice(2, 4));
    const year = Number(digits.slice(4, 8));

    // Sanity floor: no purchase/birth date predates 1900 in this app.
    if (year < 1900) {
        dateValue.value = undefined;

        return;
    }

    try {
        const candidate = new CalendarDate(year, month, day);

        // Reject overflowing dates (e.g. 31/02) that CalendarDate would normalize.
        if (candidate.day !== day || candidate.month !== month || candidate.year !== year) {
            dateValue.value = undefined;

            return;
        }

        if ((minValue && candidate.compare(minValue) < 0) || (maxValue && candidate.compare(maxValue) > 0)) {
            dateValue.value = undefined;

            return;
        }

        dateValue.value = candidate;
    } catch {
        dateValue.value = undefined;
    }
};

/** Refresh the `valid` model and the error flag from the current text. */
const evaluate = (blurred = false) => {
    syncDateFromText();

    const digits = text.value.replace(/\D/g, "");
    const complete = digits.length === 8;

    valid.value = digits.length === 0 || (complete && dateValue.value !== undefined);
    error.value = (complete && dateValue.value === undefined)
        || (blurred && digits.length > 0 && !complete);
};

const onInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const isDeleting = (event as InputEvent).inputType?.startsWith("delete") ?? false;
    const digits = target.value.replace(/\D/g, "").slice(0, 8);
    const segments = parseSegments(digits);

    // Impossible segment (day > 31, month > 12, "00"): reject the keystroke.
    if (segments === null) {
        target.value = text.value;

        return;
    }

    const formatted = formatSegments(segments, isDeleting);

    text.value = formatted;
    target.value = formatted;
    model.value = formatted;
    evaluate();
};

const onBlur = () => {
    evaluate(true);
};

const onCalendarSelect = (value?: DateValue) => {
    if (!value) {
        return;
    }

    const formatted = [
        String(value.day).padStart(2, "0"),
        String(value.month).padStart(2, "0"),
        String(value.year).padStart(4, "0")
    ].join("/");

    text.value = formatted;
    model.value = formatted;
    dateValue.value = value;
    valid.value = true;
    error.value = false;
    open.value = false;
};

watch(model, (value) => {
    if ((value ?? "") !== text.value) {
        text.value = value ?? "";
        evaluate();
    }
});

onMounted(() => evaluate());
</script>

<template>
  <div class="relative">
    <input
      :value="text"
      :placeholder="placeholder"
      :disabled="disabled"
      inputmode="numeric"
      maxlength="10"
      :aria-invalid="error"
      :title="error ? 'Date invalide' : undefined"
      :class="cn(
        'w-full border rounded-lg px-3 py-2 pr-9 text-[13px] outline-none tabular-nums',
        error ? 'border-red-400 focus:border-red-400' : 'border-slate-200',
        inputClass
      )"
      @input="onInput"
      @blur="onBlur"
    >
    <Popover v-model:open="open" modal>
      <PopoverTrigger as-child>
        <button
          type="button"
          class="absolute inset-y-0 right-0 flex items-center px-2.5 text-slate-400 hover:text-primary transition-colors"
          :disabled="disabled"
          tabindex="-1"
          aria-label="Ouvrir le calendrier"
        >
          <CalendarIcon class="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent class="w-auto p-0" align="end">
        <Calendar
          :model-value="dateValue"
          locale="fr-FR"
          :min-value="minValue"
          :max-value="maxValue"
          initial-focus
          @update:model-value="onCalendarSelect"
        />
      </PopoverContent>
    </Popover>
  </div>
</template>
